import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { submitContact } from "@/lib/api";
import { COMPANY } from "@/lib/company";

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Message received — we'll reply within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-20">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Get in touch</div>
          <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl md:text-5xl lg:text-6xl text-slate-950 max-w-3xl leading-[0.95]">
            Questions? Corporate training? Say hello.
          </h1>
          <p className="mt-6 text-slate-600 text-base md:text-lg max-w-2xl">
            Whether you&apos;re an individual learner, a company looking for cohort training, or a placement partner —
            drop us a line and we&apos;ll get back within one business day.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-14 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="grid gap-6">
            <a href={`mailto:${COMPANY.email}`} className="group border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-all block" data-testid="contact-email-card">
              <Mail className="w-6 h-6 text-[#0052FF]" />
              <div className="mt-4 text-xs uppercase tracking-widerest font-mono-plex text-slate-500">Email</div>
              <div className="mt-1 font-display text-lg font-semibold text-slate-950 break-all group-hover:text-[#0052FF] transition-colors">{COMPANY.email}</div>
            </a>
            <a href={`tel:${COMPANY.phoneDialable}`} className="group border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-all block" data-testid="contact-phone-card">
              <Phone className="w-6 h-6 text-[#0052FF]" />
              <div className="mt-4 text-xs uppercase tracking-widerest font-mono-plex text-slate-500">Phone / WhatsApp</div>
              <div className="mt-1 font-display text-lg font-semibold text-slate-950 group-hover:text-[#0052FF] transition-colors">{COMPANY.phone}</div>
            </a>
            <div className="border border-slate-200 p-6" data-testid="contact-address-card">
              <MapPin className="w-6 h-6 text-[#0052FF]" />
              <div className="mt-4 text-xs uppercase tracking-widerest font-mono-plex text-slate-500">Office</div>
              <div className="mt-1 font-display text-lg font-semibold text-slate-950">{COMPANY.address}</div>
              <div className="mt-1 text-xs text-slate-500">*Location may be updated soon — please confirm before visiting.</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div data-testid="contact-success" className="border border-slate-200 p-10 md:p-14 text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[#0052FF]" />
              <h2 className="mt-6 font-display font-bold tracking-tight text-3xl text-slate-950">Message received.</h2>
              <p className="mt-3 text-slate-600 text-base">
                Thank you, {form.full_name.split(" ")[0]}. We've got your note and will get back to you at {form.email} within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-slate-200 p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c_name">Full name*</Label>
                  <Input id="c_name" data-testid="contact-input-name" required value={form.full_name} onChange={update("full_name")} className="mt-1.5 rounded-sm" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="c_email">Email*</Label>
                  <Input id="c_email" data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className="mt-1.5 rounded-sm" placeholder="you@example.com" />
                </div>
                <div>
                  <Label htmlFor="c_phone">Phone</Label>
                  <Input id="c_phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} className="mt-1.5 rounded-sm" placeholder="+91 98xxxxxxxx" />
                </div>
                <div>
                  <Label htmlFor="c_subject">Subject*</Label>
                  <Input id="c_subject" data-testid="contact-input-subject" required value={form.subject} onChange={update("subject")} className="mt-1.5 rounded-sm" placeholder="Course inquiry, corporate training…" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="c_message">Message*</Label>
                  <Textarea id="c_message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={update("message")} className="mt-1.5 rounded-sm" placeholder="Tell us a bit about what you are looking for…" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-8 inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-medium px-8 py-3.5 transition-colors"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
